import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  inject
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { SiteUser } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AssignUsersToSiteModalComponent } from 'src/app/modals/assign-users-to-site-modal/assign-users-to-site-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-site-users',
  templateUrl: './site-users.component.html',
  styleUrls: ['./site-users.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SiteUsersComponent implements OnDestroy {
  private _cdr = inject(ChangeDetectorRef);

  suppliedGridQueryData: GenericObject = {
    User: {
      options: { required: true },
      deleted: false
    }
  };
  SiteId?: number;

  sharedDataServiceSubscription!: Subscription;
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['delete']
  };
  reloadGridData: boolean = false;

  constructor(
    private SharedDataService: SharedDataService,
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private SiteService: SiteService,
    private AuthService: AuthService
  ) {
    this.sharedDataServiceSubscription = this.SharedDataService.listenForEvent<
      number | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'setSiteId' && typeof data === 'number') {
          this.SiteId = data;
          Object.assign(this.suppliedGridQueryData, {
            SiteId: this.SiteId
          });
        }
      }
    });

    this.SharedDataService.sendEvent('getSiteId');
  }

  loggedInUser = this.AuthService.getUser();
  acceptedActionRoles: Set<string> = new Set([
    'ADMIN_ADMIN',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',

    'CLIENT_ADMIN'
  ]);

  assignUsers() {
    const modalRef = this.ModalService.open(AssignUsersToSiteModalComponent, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.SiteId = this.SiteId;
    modalRef.closed.subscribe({
      next: (SiteUser: SiteUser) => {
        if (SiteUser) {
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully assigned user.'
          );
          this.reloadGridData = true;
          this._cdr.markForCheck();
        }
      }
    });
  }

  deleteUser(SiteUser: SiteUser) {
    const modalRef = this.ModalService.open(ConfirmModalComponent, {
      centered: true
    });
    modalRef.componentInstance.displayText =
      'Are you sure you want to delete site user?';
    modalRef.componentInstance.SiteId = this.SiteId;
    modalRef.closed.subscribe({
      next: (confirm) => {
        if (confirm) {
          if (!SiteUser || !SiteUser.id) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No SiteUser to delete.'
            );
          }

          this.SiteService.deleteSiteUser(SiteUser.id).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully deleted site user.'
              );
              this.reloadGridData = true;
              this._cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  onActionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: SiteUser;
  }) {
    if (action === 'delete') {
      this.deleteUser(rowData);
    }
  }

  ngOnDestroy(): void {
    this.sharedDataServiceSubscription.unsubscribe();
  }
}
